import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  AdminListResponse,
  ApprovalAction,
  ApprovalDecisionDto,
  ApprovalDecisionType,
  ApprovalDto,
  ApprovalViewer,
  CreateApprovalBody,
  ListParams,
  RequestMeta,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/admin/approvals`;

function toQuery(params: ListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createApprovalsApi(client: ApiClient) {
  return {
    listApprovals(params: ListParams = {}) {
      return client.get<AdminListResponse<ApprovalDto>>(
        `${BASE}${toQuery(params)}`,
      );
    },
    getApproval(id: string) {
      return client.get<{
        approval: ApprovalDto;
        decisions: ApprovalDecisionDto[];
        viewer: ApprovalViewer;
        meta: RequestMeta;
      }>(`${BASE}/${id}`);
    },
    createApproval(body: CreateApprovalBody) {
      return client.post<{ approval: ApprovalDto; meta: RequestMeta }>(
        BASE,
        body,
        newIdempotencyKey(),
      );
    },
    applyApprovalAction(
      id: string,
      action: ApprovalAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
    recordDecision(
      id: string,
      body: {
        decision: ApprovalDecisionType;
        decision_reason?: string | null;
        delegation_reference?: string | null;
        break_glass_reference?: string | null;
      },
    ) {
      return client.post<{
        approval: ApprovalDto;
        decision: ApprovalDecisionDto;
        meta: RequestMeta;
      }>(`${BASE}/${id}/decisions`, body, newIdempotencyKey());
    },
  };
}

export type ApprovalsApi = ReturnType<typeof createApprovalsApi>;
